import { useEffect, useState } from "react";
import Drawer from "~/components/Drawer/Drawer";
import styles from "./FiltersDrawer.module.css";
import ChevronRight from "~/icons/ChevronRight";
import CircleCloseButton from "~/icons/CircleCloseIcon";
import Button from "~/components/Button/Button";
import BackArrowIcon from "~/icons/BackArrowIcon";

enum Step {
  Main = "main",
  Regions = "regions",
  OrderTypes = "orderTypes",
}

type Option = {
  value: string | null;
  label: string;
};

type FiltersDrawerProps = {
  sortOptions: Option[];
  originalSelectedSort: string | null;
  regionsOptions: Option[];
  originalSelectedRegion: string | null;
  orderTypesOptions: Option[];
  originalSelectedOrderType: string | null;
  visible: boolean;
  onApply: (
    sortBy: string | null,
    region: string | null,
    orderTypeCode: string | null,
  ) => void;
  onClear: () => void;
  onClose: () => void;
};

const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  sortOptions,
  originalSelectedSort,
  regionsOptions,
  originalSelectedRegion,
  orderTypesOptions,
  originalSelectedOrderType,
  visible,
  onApply,
  onClear,
  onClose,
}) => {
  const [step, setStep] = useState<Step>(Step.Main);
  const [selectedSort, setSelectedSort] = useState<string | null>(
    originalSelectedSort,
  );
  const [selectedRegion, setSelectedRegion] = useState<string | null>(
    originalSelectedRegion,
  );
  const [selectedOrderType, setSelectedOrderType] = useState<string | null>(
    originalSelectedOrderType,
  );

  useEffect(() => {
    setSelectedSort(originalSelectedSort);
    setSelectedRegion(originalSelectedRegion);
    setSelectedOrderType(originalSelectedOrderType);
  }, [originalSelectedSort, originalSelectedRegion, originalSelectedOrderType]);

  const handleApply = () => {
    if (step === Step.Main) {
      onApply(selectedSort, selectedRegion, selectedOrderType);
      onClose();
    } else if (step === Step.Regions) {
      setStep(Step.Main);
    } else if (step === Step.OrderTypes) {
      setStep(Step.Main);
    }
  };

  const handleClear = () => {
    if (step === Step.Main) {
      setSelectedSort(null);
      setSelectedRegion(null);
      setSelectedOrderType(null);
      onClear();
      onClose();
    } else if (step === Step.Regions) {
      setSelectedRegion(null);
    } else if (step === Step.OrderTypes) {
      setSelectedOrderType(null);
    }
  };

  const getRegionLabel = () =>
    selectedRegion
      ? regionsOptions.find((opt) => opt.value === selectedRegion)?.label
      : "All regions";

  const getOrderTypeLabel = () =>
    selectedOrderType
      ? orderTypesOptions.find((opt) => opt.value === selectedOrderType)?.label
      : "All order types";

  return (
    <Drawer
      dialogClassName={styles.dialog}
      hideHeader
      visible={visible}
      onClose={onClose}
    >
      <div className={styles.container}>
        {step === Step.Main && (
          <>
            <div className={styles.header}>
              <span>Filters</span>
              <button onClick={onClose} className={styles.closeButton}>
                <CircleCloseButton color="#828282" />
              </button>
            </div>

            <div className={styles.content}>
              <div className={styles.filtersSection}>
                <div
                  className={styles.filterItem}
                  onClick={() => setStep(Step.Regions)}
                >
                  <span>{getRegionLabel()}</span>
                  <ChevronRight />
                </div>
                <div
                  className={styles.filterItem}
                  onClick={() => setStep(Step.OrderTypes)}
                >
                  <span>{getOrderTypeLabel()}</span>
                  <ChevronRight />
                </div>
              </div>

              <div className={styles.sortSection}>
                <div className={styles.sectionTitle}>Sort</div>
                {sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={styles.optionItem}
                    onClick={() => setSelectedSort(option.value)}
                  >
                    <span>{option.label}</span>
                    <div className={styles.radioButton}>
                      {selectedSort === option.value && (
                        <div className={styles.radioButtonInner} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {step === Step.Regions && (
          <>
            <div className={styles.header}>
              <button
                onClick={() => setStep(Step.Main)}
                className={styles.backButton}
              >
                <BackArrowIcon />
              </button>
              <span>Filter by regions</span>
              <button onClick={onClose} className={styles.closeButton}>
                <CircleCloseButton color="#828282" />
              </button>
            </div>

            <div className={styles.content}>
              {regionsOptions.map((option) => (
                <div
                  key={option.value}
                  className={styles.optionItem}
                  onClick={() => setSelectedRegion(option.value)}
                >
                  <span>{option.label}</span>
                  <div className={styles.radioButton}>
                    {selectedRegion === option.value && (
                      <div className={styles.radioButtonInner} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {step === Step.OrderTypes && (
          <>
            <div className={styles.header}>
              <button
                onClick={() => setStep(Step.Main)}
                className={styles.backButton}
              >
                <BackArrowIcon />
              </button>
              <span>Filter by order types</span>
              <button onClick={onClose} className={styles.closeButton}>
                <CircleCloseButton color="#828282" />
              </button>
            </div>

            <div className={styles.content}>
              {orderTypesOptions.map((option) => (
                <div
                  key={option.value}
                  className={styles.optionItem}
                  onClick={() => setSelectedOrderType(option.value)}
                >
                  <span>{option.label}</span>
                  <div className={styles.radioButton}>
                    {selectedOrderType === option.value && (
                      <div className={styles.radioButtonInner} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className={styles.footer}>
          <Button className={styles.applyButton} onClick={handleApply}>
            Apply
          </Button>
          <Button
            variant="secondary"
            className={styles.clearButton}
            onClick={handleClear}
          >
            {step === Step.Main ? "Clear all" : "Clear"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default FiltersDrawer;
